import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { CURRENCY_SYMBOLS, CURRENCY_TYPE } from '../../enum/constants';
import {
  StockDocument,
  StockDocumentStatus,
  StockDocumentType,
  StockShortage,
  VariantSummary,
  WarehouseCategory,
} from './warehouse.types';

/** Pages of the warehouse section: its tabs and the left menu */
export const WAREHOUSE_PAGES = [
  { path: 'products', tkey: 'warehouse_products', icon: 'inventory_2' },
  { path: 'stock', tkey: 'warehouse_stock', icon: 'stacked_bar_chart' },
  { path: 'documents', tkey: 'warehouse_documents', icon: 'description' },
  { path: 'movements', tkey: 'warehouse_movements', icon: 'receipt_long' },
  { path: 'directories', tkey: 'warehouse_directories', icon: 'tune' },
];

export const CURRENCY_OPTIONS = [CURRENCY_TYPE.UAH, CURRENCY_TYPE.USD, CURRENCY_TYPE.EUR].map((value) => ({
  value,
  symbol: getCurrencySymbol(value),
  label: `${value} ${getCurrencySymbol(value)}`,
}));

export const DOCUMENT_TYPE_STYLES: Record<StockDocumentType, { icon: string; class: string }> = {
  RECEIPT: { icon: 'move_to_inbox', class: 'bg-emerald-50 text-emerald-700' },
  WRITE_OFF: { icon: 'outbox', class: 'bg-rose-50 text-rose-700' },
  TRANSFER: { icon: 'local_shipping', class: 'bg-sky-50 text-sky-700' },
  ADJUSTMENT: { icon: 'tune', class: 'bg-violet-50 text-violet-700' },
  INVENTORY: { icon: 'fact_check', class: 'bg-amber-50 text-amber-700' },
  WRITE_OFF_CANCEL: { icon: 'undo', class: 'bg-slate-100 text-slate-700' },
};

export const DOCUMENT_STATUS_CLASSES: Record<StockDocumentStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  POSTED: 'bg-emerald-50 text-emerald-700',
  CANCELLED: 'bg-rose-50 text-rose-600',
};

/** `app-select` options of API values, labelled by translation keys */
export const toOptions = <T extends string>(values: readonly T[], prefix: string): { value: T; label: string }[] =>
  values.map((value) => ({ value, label: prefix + value }));

export function getApiError(error: unknown): { code?: string; details?: Record<string, unknown> } {
  return ((error as HttpErrorResponse | null)?.error as { errors?: object[] } | null)?.errors?.[0] ?? {};
}

/** `details.items` of 409 INSUFFICIENT_STOCK, empty for other errors */
export function getStockShortages(error: unknown): StockShortage[] {
  const { code, details } = getApiError(error);
  return code === 'INSUFFICIENT_STOCK' && Array.isArray(details?.['items'])
    ? (details['items'] as StockShortage[])
    : [];
}

export function escapeHtml(value: string | null | undefined): string {
  return String(value ?? '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}

/** Up to 3 decimals without trailing zeros, thousands split by spaces: 1 250.5 */
export function formatQuantity(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';

  const [integerPart, fractionPart] = String(Math.round(value * 1000) / 1000).split('.');
  const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return fractionPart ? `${grouped}.${fractionPart}` : grouped;
}

/** Signed quantity of a movement or an adjustment: +5, −2.5 */
export function formatSignedQuantity(value: number): string {
  return value > 0 ? `+${formatQuantity(value)}` : value < 0 ? `−${formatQuantity(-value)}` : '0';
}

export function getCurrencySymbol(currency: string | null | undefined): string {
  return CURRENCY_SYMBOLS[currency ?? ''] ?? currency ?? '';
}

/**
 * A typed number: spaces are dropped and a comma is a decimal point. Null unless it has at most `decimals` decimals
 * (3 for quantities, 4 for prices) and is negative only when `signed`.
 */
export function parseNumber(value: string | null | undefined, decimals: number, signed = false): number | null {
  const text = (value ?? '').replace(/\s+/g, '').replace(',', '.');
  return new RegExp(`^${signed ? '-?' : ''}\\d+(\\.\\d{1,${decimals}})?$`).test(text) ? Number(text) : null;
}

/** «Футболка · Червоний / M» */
export function getVariantTitle(variant: Pick<VariantSummary, 'product_name' | 'name'> | null | undefined): string {
  if (!variant) return '';
  return variant.name ? `${variant.product_name} · ${variant.name}` : variant.product_name;
}

export function getVariantCodes(variant: Pick<VariantSummary, 'sku' | 'barcode'> | null | undefined): string {
  return [variant?.sku, variant?.barcode].filter(Boolean).join(' · ');
}

/** Files of the file system are public by id */
export function getFileUrl(fileId: string | null | undefined): string | null {
  return fileId ? `${environment.baseURL}/file-system/assets/${fileId}?thumbnail=true` : null;
}

export const getDocumentNumber = (documentId: string): string => `#${documentId.slice(0, 8).toUpperCase()}`;

export const isOpenTransfer = (document: StockDocument | null): boolean =>
  document?.type === 'TRANSFER' && document.status === 'POSTED' && !document.closed_at;

/** ISO bounds of a picked day in local time, for the inclusive `date_from` / `date_to` filters */
export function toDayIso(date: Date | null | undefined, end = false): string | null {
  if (!date) return null;
  const day = new Date(date);
  day.setHours(end ? 23 : 0, end ? 59 : 0, end ? 59 : 0, end ? 999 : 0);
  return day.toISOString();
}

export interface CategoryNode extends WarehouseCategory {
  /** Name with the path to it, to tell apart categories with the same name */
  path: string;
  depth: number;
  /** Categories above it, the root first */
  parentIds: string[];
  hasChildren: boolean;
}

/** Categories in tree order: parents before their children, siblings by name */
export function getCategoryTree(categories: WarehouseCategory[]): CategoryNode[] {
  const ids = new Set(categories.map((category) => category.id));
  // A parent that is not in the list puts the category to the root
  const parentOf = (category: WarehouseCategory): string | null =>
    category.parent_id && ids.has(category.parent_id) ? category.parent_id : null;

  const nodes: CategoryNode[] = [];
  const visit = (parentId: string | null, parents: CategoryNode[]): void =>
    categories
      .filter((category) => parentOf(category) === parentId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((category) => {
        const node: CategoryNode = {
          ...category,
          path: [...parents.map((parent) => parent.name), category.name].join(' / '),
          depth: parents.length,
          parentIds: parents.map((parent) => parent.id),
          hasChildren: categories.some((child) => parentOf(child) === category.id),
        };
        nodes.push(node);
        visit(category.id, [...parents, node]);
      });
  visit(null, []);

  return nodes;
}
